// Admin: company settings (legal name, address, header/seal images) and bank accounts
// Cabecera y sello: misma galería y subida que en quotation-requests/:id (quotation_header_images / quotation_seal_images + store-assets).

import { useState, useEffect, useCallback } from 'react';
import {
  useCompanySettings,
  useUpsertCompanySettings,
  useCompanyBankAccounts,
  useUpsertCompanyBankAccount,
  useDeleteCompanyBankAccount,
  useQuotationHeaderImages,
  useCreateQuotationHeaderImage,
  useDeleteQuotationHeaderImage,
  useQuotationSealImages,
  useCreateQuotationSealImage,
  useDeleteQuotationSealImage,
} from '@/hooks/use-store';
import type { CompanyBankAccount } from '@/lib/store-types';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Landmark, Plus, Trash2, Upload, Check, X, Image as ImageIcon, Pencil } from 'lucide-react';

const EMPTY_BANK_FORM = {
  label: '',
  bank_name: '',
  account_holder: '',
  account_number: '',
  iban: '',
  swift: '',
  currency: '',
  account_type: '',
  details: '',
  beneficiary_bank_address: '',
  intermediary_bank_name: '',
  intermediary_bank_address: '',
  intermediary_bank_swift: '',
};

export default function AdminCompanySettings() {
  const { data: settings, isLoading } = useCompanySettings();
  const { data: accounts, isLoading: accountsLoading } = useCompanyBankAccounts();
  const { data: headerImages } = useQuotationHeaderImages();
  const createHeaderImage = useCreateQuotationHeaderImage();
  const deleteHeaderImage = useDeleteQuotationHeaderImage();
  const { data: sealImages } = useQuotationSealImages();
  const createSealImage = useCreateQuotationSealImage();
  const deleteSealImage = useDeleteQuotationSealImage();
  const upsertSettings = useUpsertCompanySettings();
  const upsertAccount = useUpsertCompanyBankAccount();
  const deleteAccount = useDeleteCompanyBankAccount();
  const { toast } = useToast();

  const [legalName, setLegalName] = useState('');
  const [entityCode, setEntityCode] = useState('');
  const [address, setAddress] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [sealImageUrl, setSealImageUrl] = useState('');
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingSeal, setUploadingSeal] = useState(false);

  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<CompanyBankAccount | null>(null);
  const [bankForm, setBankForm] = useState(EMPTY_BANK_FORM);

  useEffect(() => {
    if (!settings) return;
    setLegalName(settings.legal_name ?? '');
    setEntityCode(settings.entity_code ?? '');
    setAddress(settings.address ?? '');
    setHeaderImageUrl(settings.header_image_url ?? '');
    setSealImageUrl(settings.seal_image_url ?? '');
  }, [settings]);

  /** Sube imagen a store-assets (quotation-docs/headers) y la registra en quotation_header_images; actualiza solo estado local (se persiste al guardar empresa). */
  const handleHeaderUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingHeader(true);
      try {
        const uid = crypto.randomUUID().slice(0, 8);
        const ext = file.name.split('.').pop() || 'png';
        const cleanName = file.name.replace(/\.[^/.]+$/, '').slice(0, 40);
        const path = `quotation-docs/headers/${uid}.${ext}`;
        const { error } = await supabase.storage.from('store-assets').upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('store-assets').getPublicUrl(path);
        await createHeaderImage.mutateAsync({ name: cleanName || 'Cabecera', url: urlData.publicUrl });
        setHeaderImageUrl(urlData.publicUrl);
        toast({ title: 'Imagen de cabecera subida' });
      } catch (err) {
        toast({ title: 'Error subiendo imagen', description: String(err), variant: 'destructive' });
      } finally {
        setUploadingHeader(false);
      }
    },
    [createHeaderImage, toast]
  );

  /** Sube sello a store-assets (quotation-docs/seals) y lo registra en quotation_seal_images; actualiza solo estado local (se persiste al guardar empresa). */
  const handleSealUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingSeal(true);
      try {
        const uid = crypto.randomUUID().slice(0, 8);
        const ext = file.name.split('.').pop() || 'png';
        const cleanName = file.name.replace(/\.[^/.]+$/, '').slice(0, 40);
        const path = `quotation-docs/seals/${uid}.${ext}`;
        const { error } = await supabase.storage.from('store-assets').upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('store-assets').getPublicUrl(path);
        await createSealImage.mutateAsync({ name: cleanName || 'Sello', url: urlData.publicUrl });
        setSealImageUrl(urlData.publicUrl);
        toast({ title: 'Sello subido' });
      } catch (err) {
        toast({ title: 'Error subiendo sello', description: String(err), variant: 'destructive' });
      } finally {
        setUploadingSeal(false);
      }
    },
    [createSealImage, toast]
  );

  const handleSaveSettings = () => {
    upsertSettings.mutate(
      {
        id: settings?.id,
        legal_name: legalName.trim() || undefined,
        entity_code: entityCode.trim() || null,
        address: address.trim() || null,
        header_image_url: headerImageUrl.trim() || null,
        seal_image_url: sealImageUrl.trim() || null,
      },
      {
        onSuccess: () => toast({ title: 'Datos de empresa guardados' }),
        onError: (e) => toast({ title: 'Error al guardar', description: String(e), variant: 'destructive' }),
      }
    );
  };

  const handleDeleteAccount = (id: string) => {
    if (!confirm('¿Eliminar esta cuenta bancaria?')) return;
    deleteAccount.mutate(id, {
      onSuccess: () => toast({ title: 'Cuenta eliminada' }),
      onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }),
    });
  };

  const openBankDialog = (account?: CompanyBankAccount | null) => {
    if (account) {
      setEditingBankAccount(account);
      setBankForm({
        label: account.label ?? '',
        bank_name: account.bank_name ?? '',
        account_holder: account.account_holder ?? '',
        account_number: account.account_number ?? '',
        iban: account.iban ?? '',
        swift: account.swift ?? '',
        currency: account.currency ?? '',
        account_type: account.account_type ?? '',
        details: account.details ?? '',
        beneficiary_bank_address: account.beneficiary_bank_address ?? '',
        intermediary_bank_name: account.intermediary_bank_name ?? '',
        intermediary_bank_address: account.intermediary_bank_address ?? '',
        intermediary_bank_swift: account.intermediary_bank_swift ?? '',
      });
    } else {
      setEditingBankAccount(null);
      setBankForm(EMPTY_BANK_FORM);
    }
    setBankDialogOpen(true);
  };

  const handleSaveBankAccount = () => {
    const payload: Partial<CompanyBankAccount> = {
      label: bankForm.label.trim() || 'Cuenta',
      bank_name: bankForm.bank_name.trim() || null,
      account_holder: bankForm.account_holder.trim() || null,
      account_number: bankForm.account_number.trim() || null,
      iban: bankForm.iban.trim() || null,
      swift: bankForm.swift.trim() || null,
      currency: bankForm.currency.trim() || null,
      account_type: bankForm.account_type.trim() || null,
      details: bankForm.details.trim() || null,
      beneficiary_bank_address: bankForm.beneficiary_bank_address.trim() || null,
      intermediary_bank_name: bankForm.intermediary_bank_name.trim() || null,
      intermediary_bank_address: bankForm.intermediary_bank_address.trim() || null,
      intermediary_bank_swift: bankForm.intermediary_bank_swift.trim() || null,
    };
    if (editingBankAccount?.id) payload.id = editingBankAccount.id;
    upsertAccount.mutate(payload, {
      onSuccess: () => {
        toast({ title: editingBankAccount ? 'Cuenta actualizada' : 'Cuenta añadida' });
        setBankDialogOpen(false);
      },
      onError: (e) => toast({ title: 'Error', description: String(e), variant: 'destructive' }),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Landmark className="h-6 w-6" />
          Empresa y cuentas bancarias
        </h1>
        <p className="text-zinc-600 mt-1">
          Datos de la empresa (cabecera, sello) y cuentas bancarias para cotizaciones.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Datos de la empresa</CardTitle>
          <p className="text-sm text-zinc-500 font-normal">
            Nombre legal, código de entidad, dirección e imágenes para cabecera y sello en documentos.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="legal_name">Nombre legal</Label>
              <Input
                id="legal_name"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Ej. Padmora S.L."
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="entity_code">Código de entidad</Label>
              <Input
                id="entity_code"
                value={entityCode}
                onChange={(e) => setEntityCode(e.target.value)}
                placeholder="Opcional"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Dirección completa</Label>
            <Textarea
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Dirección fiscal"
              className="mt-1 min-h-[80px]"
            />
          </div>
          <div>
            <Label className="text-xs">Imagen de cabecera (documentos / oferta digital)</Label>
            <div className="mt-1">
              <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-primary hover:underline mb-2">
                <Upload className="h-4 w-4" />
                {uploadingHeader ? 'Subiendo...' : 'Subir nueva imagen'}
                <input type="file" accept="image/*" className="hidden" onChange={handleHeaderUpload} disabled={uploadingHeader} />
              </label>
              {headerImages && headerImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {headerImages.map((img) => {
                    const isSelected = headerImageUrl === img.url;
                    return (
                      <div
                        key={img.id}
                        className={`relative group cursor-pointer rounded-md border-2 transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-zinc-200 hover:border-zinc-400'}`}
                        onClick={() => setHeaderImageUrl(img.url)}
                        title={img.name}
                      >
                        <img src={img.url} alt={img.name} className="h-14 w-28 object-cover rounded-[5px]" />
                        {isSelected && (
                          <div className="absolute top-0.5 left-0.5 bg-primary text-white rounded-full p-0.5">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        <button
                          type="button"
                          className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:border-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!confirm('¿Eliminar esta imagen de la galería?')) return;
                            deleteHeaderImage.mutate(img.id, {
                              onSuccess: () => {
                                if (headerImageUrl === img.url) setHeaderImageUrl('');
                                toast({ title: 'Imagen eliminada de la galería' });
                              },
                            });
                          }}
                          title="Eliminar de galería"
                        >
                          <X className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {headerImageUrl && !(headerImages ?? []).some((i) => i.url === headerImageUrl) && (
                <div className="flex items-center gap-2 mt-2">
                  <ImageIcon className="h-4 w-4 text-zinc-400" />
                  <img src={headerImageUrl} alt="Cabecera actual" className="h-10 rounded border border-zinc-200 max-w-[200px] object-contain" />
                  <span className="text-xs text-zinc-400">(URL en uso)</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Sello para el cierre (documentos / oferta digital)</Label>
            <div className="mt-1">
              <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-primary hover:underline mb-2">
                <Upload className="h-4 w-4" />
                {uploadingSeal ? 'Subiendo...' : 'Subir nuevo sello'}
                <input type="file" accept="image/*" className="hidden" onChange={handleSealUpload} disabled={uploadingSeal} />
              </label>
              {sealImages && sealImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {sealImages.map((img) => {
                    const isSelected = sealImageUrl === img.url;
                    return (
                      <div
                        key={img.id}
                        className={`relative group cursor-pointer rounded-md border-2 transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-zinc-200 hover:border-zinc-400'}`}
                        onClick={() => setSealImageUrl(img.url)}
                        title={img.name}
                      >
                        <img src={img.url} alt={img.name} className="h-14 w-14 object-contain rounded-[5px] bg-zinc-50" />
                        {isSelected && (
                          <div className="absolute top-0.5 left-0.5 bg-primary text-white rounded-full p-0.5">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        <button
                          type="button"
                          className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:border-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!confirm('¿Eliminar este sello de la galería?')) return;
                            deleteSealImage.mutate(img.id, {
                              onSuccess: () => {
                                if (sealImageUrl === img.url) setSealImageUrl('');
                                toast({ title: 'Sello eliminado de la galería' });
                              },
                            });
                          }}
                          title="Eliminar de galería"
                        >
                          <X className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {sealImageUrl && !(sealImages ?? []).some((i) => i.url === sealImageUrl) && (
                <div className="flex items-center gap-2 mt-2">
                  <ImageIcon className="h-4 w-4 text-zinc-400" />
                  <img src={sealImageUrl} alt="Sello actual" className="h-10 w-10 rounded border border-zinc-200 object-contain bg-zinc-50" />
                  <span className="text-xs text-zinc-400">(URL en uso)</span>
                </div>
              )}
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={upsertSettings.isPending}>
            {upsertSettings.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar datos de empresa'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cuentas bancarias</CardTitle>
          <p className="text-sm text-zinc-500 font-normal">
            Cuentas para transferencia o crypto; asignables por cotización.
          </p>
        </CardHeader>
        <CardContent>
          {accountsLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          ) : (accounts?.length ?? 0) === 0 ? (
            <p className="text-zinc-500 text-sm">Aún no hay cuentas. Añade una con el botón.</p>
          ) : (
            <ul className="space-y-3">
              {(accounts ?? []).map((acc) => (
                <li
                  key={acc.id}
                  className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/50 p-3"
                >
                  <div>
                    <p className="font-medium text-zinc-900">{acc.label}</p>
                    {(acc.bank_name || acc.iban || acc.account_number) && (
                      <p className="text-sm text-zinc-600 mt-0.5">
                        {[acc.bank_name, acc.iban ?? acc.account_number].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-zinc-500 hover:text-zinc-700"
                      onClick={() => openBankDialog(acc)}
                      disabled={upsertAccount.isPending}
                      title="Editar cuenta"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-zinc-500 hover:text-red-600"
                      onClick={() => handleDeleteAccount(acc.id)}
                      disabled={deleteAccount.isPending}
                      title="Eliminar cuenta"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => openBankDialog()}
              disabled={upsertAccount.isPending}
            >
              <Plus className="h-4 w-4" />
              Añadir cuenta bancaria
            </Button>
          </div>

          <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
            <DialogContent className="sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>{editingBankAccount ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}</DialogTitle>
                <DialogDescription>
                  Datos para transferencias internacionales (TT). Rellena al menos los campos que uses para recibir pagos.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-6 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                    Datos principales de la cuenta
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <Label htmlFor="bank_label">Nombre / etiqueta de la cuenta</Label>
                      <Input
                        id="bank_label"
                        value={bankForm.label}
                        onChange={(e) => setBankForm((f) => ({ ...f, label: e.target.value }))}
                        placeholder="Ej. Cuenta principal USD"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_name">Banco</Label>
                      <Input
                        id="bank_name"
                        value={bankForm.bank_name}
                        onChange={(e) => setBankForm((f) => ({ ...f, bank_name: e.target.value }))}
                        placeholder="Nombre del banco"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_holder">Titular / beneficiario</Label>
                      <Input
                        id="bank_holder"
                        value={bankForm.account_holder}
                        onChange={(e) => setBankForm((f) => ({ ...f, account_holder: e.target.value }))}
                        placeholder="Nombre del titular de la cuenta"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_number">Número de cuenta</Label>
                      <Input
                        id="bank_number"
                        value={bankForm.account_number}
                        onChange={(e) => setBankForm((f) => ({ ...f, account_number: e.target.value }))}
                        placeholder="Número de cuenta local (si aplica)"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_currency">Moneda</Label>
                      <Input
                        id="bank_currency"
                        value={bankForm.currency}
                        onChange={(e) => setBankForm((f) => ({ ...f, currency: e.target.value }))}
                        placeholder="USD, EUR..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_type">Tipo de cuenta</Label>
                      <Input
                        id="bank_type"
                        value={bankForm.account_type}
                        onChange={(e) => setBankForm((f) => ({ ...f, account_type: e.target.value }))}
                        placeholder="wire, local, crypto..."
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                      Banco beneficiario
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="bank_iban">IBAN</Label>
                        <Input
                          id="bank_iban"
                          value={bankForm.iban}
                          onChange={(e) => setBankForm((f) => ({ ...f, iban: e.target.value }))}
                          placeholder="IBAN (transferencias internacionales)"
                          className="mt-1 font-mono"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_swift">SWIFT / BIC</Label>
                        <Input
                          id="bank_swift"
                          value={bankForm.swift}
                          onChange={(e) => setBankForm((f) => ({ ...f, swift: e.target.value }))}
                          placeholder="Código SWIFT/BIC del banco"
                          className="mt-1 font-mono"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_beneficiary_address">Dirección del banco beneficiario</Label>
                        <Textarea
                          id="bank_beneficiary_address"
                          value={bankForm.beneficiary_bank_address}
                          onChange={(e) => setBankForm((f) => ({ ...f, beneficiary_bank_address: e.target.value }))}
                          placeholder="Opcional. Dirección del banco donde está la cuenta"
                          className="mt-1 min-h-[72px]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_details">Detalles adicionales</Label>
                        <Textarea
                          id="bank_details"
                          value={bankForm.details}
                          onChange={(e) => setBankForm((f) => ({ ...f, details: e.target.value }))}
                          placeholder="Routing, notas internas..."
                          className="mt-1 min-h-[60px]"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                      Banco intermediario / corresponsal
                    </p>
                    <p className="text-xs text-zinc-500 mb-3">
                      Opcional. Para transferencias internacionales (TT) que pasan por un banco intermediario.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="bank_intermediary_name">Nombre del banco intermediario</Label>
                        <Input
                          id="bank_intermediary_name"
                          value={bankForm.intermediary_bank_name}
                          onChange={(e) => setBankForm((f) => ({ ...f, intermediary_bank_name: e.target.value }))}
                          placeholder="Ej. JP Morgan Chase"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_intermediary_address">Dirección del banco intermediario</Label>
                        <Textarea
                          id="bank_intermediary_address"
                          value={bankForm.intermediary_bank_address}
                          onChange={(e) => setBankForm((f) => ({ ...f, intermediary_bank_address: e.target.value }))}
                          placeholder="Opcional"
                          className="mt-1 min-h-[60px]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bank_intermediary_swift">SWIFT del banco intermediario</Label>
                        <Input
                          id="bank_intermediary_swift"
                          value={bankForm.intermediary_bank_swift}
                          onChange={(e) => setBankForm((f) => ({ ...f, intermediary_bank_swift: e.target.value }))}
                          placeholder="Ej. CHASUS33XXX"
                          className="mt-1 font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBankDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveBankAccount} disabled={upsertAccount.isPending || !bankForm.label.trim()}>
                  {upsertAccount.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {editingBankAccount ? 'Guardar cambios' : 'Añadir cuenta'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
}
